import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';

// Tour steps configuration
// Each step shows a tooltip and optionally highlights a target element
const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Get clear in 30 seconds',
    content: 'Add your OKRs, then DecisionOS shows your alignment level (aka Drift score) and the single best next step to keep you on track.',
    target: null,
    position: 'center',
    route: '/overview'
  },
  {
    id: 'drift',
    title: 'Drift (Alignment with OKRs)',
    content: 'Drift estimates how aligned your recent work is with your OKRs.',
    bullets: [
      'Higher Drift = too many unlinked tasks',
      'Link captures to a KR to lower Drift',
      'No estimate until you have a few captures'
    ],
    target: '.drift-ring-container-v2',
    position: 'right',
    route: '/overview'
  },
  {
    id: 'next-step',
    title: 'Your Next Step',
    content: 'The single most important action right now, based on your OKRs. Click it to see your action plan.',
    target: '.next-step-element, .next-step-trigger, [data-tour="next-step"], .hero-next-step',
    position: 'left',
    route: '/overview'
  },
  {
    id: 'nav-editor',
    title: 'OKR Editor',
    content: 'Use the sidebar to navigate. Click "OKR Editor" to add your goals — this is where everything starts.',
    target: '[data-nav="editor"]',
    position: 'right',
    route: '/overview',
    nextRoute: '/editor' // Navigate here AFTER clicking Next
  },
  {
    id: 'okr-editor',
    title: 'Add your OKRs',
    content: 'Paste them from a doc, or teach DecisionOS your goals one at a time.',
    target: '.mode-selection, .okr-editor-content-block',
    position: 'bottom',
    route: '/editor'
  },
  {
    id: 'nav-plan',
    title: 'My Plan',
    content: 'Click "My Plan" in the sidebar to see your command center for tracking progress.',
    target: '[data-nav="plan"]',
    position: 'right',
    route: '/editor',
    nextRoute: '/plan' // Navigate here AFTER clicking Next
  },
  {
    id: 'my-plan',
    title: 'Your command center',
    content: "Once you've added OKRs, come here to update progress, link tasks, and track what matters.",
    target: '.my-okrs-section, .section-card',
    position: 'bottom',
    route: '/plan'
  },
  {
    id: 'complete',
    title: "You're ready",
    content: "Start by adding your OKRs. Once they're in, DecisionOS will guide your focus every day.",
    target: null,
    position: 'center',
    route: '/editor',
    finalCTA: 'Add OKRs now',
    finalRoute: '/editor'
  }
];

function OnboardingTour({ isActive, onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetElement, setTargetElement] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const navigate = useNavigate();
  const tooltipRef = useRef(null);
  const positionTimeoutRef = useRef(null);

  const step = TOUR_STEPS[currentStep] || null;
  const totalSteps = TOUR_STEPS.length;

  // Calculate tooltip position relative to target
  const calculatePosition = useCallback(() => {
    if (!tooltipRef.current || !step) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 20;
    const gap = 16; // Extra gap between tooltip and target

    // Center position for steps without targets
    if (!targetRect || step.position === 'center') {
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 1
      });
      return;
    }

    const tooltipWidth = tooltipRect.width || 380;
    const tooltipHeight = tooltipRect.height || 220;

    let top = 0;
    let left = 0;

    // Available space around target
    const spaceRight = viewportWidth - targetRect.right - padding;
    const spaceLeft = targetRect.left - padding;
    const spaceAbove = targetRect.top - padding;
    const spaceBelow = viewportHeight - targetRect.bottom - padding;

    const neededWidth = tooltipWidth + gap;
    const neededHeight = tooltipHeight + gap;

    const fits = {
      right: spaceRight >= neededWidth,
      left: spaceLeft >= neededWidth,
      top: spaceAbove >= neededHeight,
      bottom: spaceBelow >= neededHeight
    };

    // Choose best position: prefer step.position, then largest available space
    const preferred = step.position;
    let pos = preferred;
    if (!fits[preferred]) {
      const options = ['right', 'left', 'bottom', 'top'];
      pos = options.reduce((best, option) => {
        const space = option === 'right' ? spaceRight
          : option === 'left' ? spaceLeft
          : option === 'bottom' ? spaceBelow
          : spaceAbove;
        const bestSpace = best === 'right' ? spaceRight
          : best === 'left' ? spaceLeft
          : best === 'bottom' ? spaceBelow
          : spaceAbove;
        return space > bestSpace ? option : best;
      }, options[0]);
    }

    if (pos === 'right') {
      left = targetRect.right + gap;
      top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
    } else if (pos === 'left') {
      left = targetRect.left - tooltipWidth - gap;
      top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
    } else if (pos === 'bottom') {
      top = targetRect.bottom + gap;
      left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
    } else if (pos === 'top') {
      top = targetRect.top - tooltipHeight - gap;
      left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
    }

    // Keep tooltip in viewport
    if (left < padding) left = padding;
    if (left + tooltipWidth > viewportWidth - padding) left = viewportWidth - tooltipWidth - padding;
    if (top < padding) top = padding;
    if (top + tooltipHeight > viewportHeight - padding) top = viewportHeight - tooltipHeight - padding;

    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      transform: 'none',
      opacity: 1
    });
  }, [step, targetRect]);

  // Find target element with retry logic
  const findTarget = useCallback(() => {
    if (!step || !step.target) {
      setTargetElement(null);
      return;
    }

    const selectors = step.target.split(',').map(s => s.trim());
    
    const tryFind = (attempts = 0) => {
      for (const selector of selectors) {
        const el = document.querySelector(selector);
        if (el) {
          const rect = el.getBoundingClientRect();
          setTargetElement(el);
          setTargetRect(rect);
          return;
        }
      }
      
      // If not found, retry a few times (DOM may still be loading)
      if (attempts < 10) {
        positionTimeoutRef.current = setTimeout(() => tryFind(attempts + 1), 100);
      } else {
        // Give up - proceed without target highlight
        setTargetElement(null);
        setTargetRect(null);
      }
    };

    tryFind();
  }, [step]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (positionTimeoutRef.current) {
        clearTimeout(positionTimeoutRef.current);
      }
    };
  }, []);

  // Handle step changes and navigation
  useEffect(() => {
    if (!isActive || !step) return;

    setIsReady(false);
    setTargetElement(null);
    setTargetRect(null);

    const currentRoute = window.location.pathname;
    const needsNavigation = step.route && step.route !== currentRoute;

    if (needsNavigation) {
      navigate(step.route);
      const timer = setTimeout(() => {
        findTarget();
        setIsReady(true);
      }, 300);
      return () => clearTimeout(timer);
    }

    findTarget();
    setTimeout(() => setIsReady(true), 80);
  }, [isActive, currentStep, navigate, findTarget, step]);

  // Recalculate position when target changes or window resizes
  useEffect(() => {
    if (!isActive || !isReady) return;

    // Small delay to ensure DOM is settled
    const timer = setTimeout(calculatePosition, 80);

    const handleResize = () => {
      clearTimeout(positionTimeoutRef.current);
      positionTimeoutRef.current = setTimeout(calculatePosition, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [isActive, isReady, targetElement, calculatePosition]);

  const handleNext = () => {
    // If this step has a nextRoute, navigate there
    if (step.nextRoute) {
      navigate(step.nextRoute);
    }
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinalCTA = () => {
    if (step.finalRoute) {
      navigate(step.finalRoute);
    }
    onComplete();
  };

  if (!isActive || !step) return null;

  return (
    <div className="tour-container">
      {/* Backdrop - always visible for focus */}
      <div className="tour-backdrop" onClick={onSkip} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`tour-tooltip-v3 ${isReady ? 'tour-tooltip-visible' : ''}`}
        style={tooltipStyle}
      >

        {/* Header */}
        <div className="tour-header-v3">
          <div className="tour-header-top">
            <span className="tour-step-indicator">
              {currentStep + 1} of {totalSteps}
            </span>
            <button className="tour-skip-btn" onClick={onSkip}>
              Skip tour
            </button>
          </div>
          <h3 className="tour-title-v3">{step.title}</h3>
        </div>

        {/* Content */}
        <div className="tour-content-v3">
          <p>{step.content}</p>
          {step.bullets && step.bullets.length > 0 && (
            <ul className="tour-bullets-v3">
              {step.bullets.map((bullet, idx) => (
                <li key={idx}>{bullet}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Actions - simple Next/Back buttons only */}
        <div className="tour-actions-v3">
          {currentStep > 0 && !step.finalCTA && (
            <button className="tour-btn-secondary" onClick={handlePrevious}>
              Back
            </button>
          )}
          
          {step.finalCTA ? (
            <button className="tour-btn-primary tour-btn-cta" onClick={handleFinalCTA}>
              {step.finalCTA}
            </button>
          ) : (
            <button className="tour-btn-primary" onClick={handleNext}>
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingTour;
